import type RTKClient from "@cloudflare/realtimekit";
import type { RTKParticipant } from "@cloudflare/realtimekit";
import { RTK_HOST_PRESET } from "./realtimeKitPresets";

const LIVESTREAM_ADMIN_NAME_SUFFIX = " (Admin)";

export function isLivestreamAdminParticipant({
  participant,
}: {
  participant: Pick<RTKParticipant, "name">;
}): boolean {
  const name = participant.name?.trim() ?? "";
  return name.endsWith(LIVESTREAM_ADMIN_NAME_SUFFIX);
}

export function hasHostLivestream({ participant }: { participant: RTKParticipant }): boolean {
  const hasScreenShare =
    participant.screenShareEnabled && Boolean(participant.screenShareTracks?.video);
  const hasCamera = participant.videoEnabled && Boolean(participant.videoTrack);
  const hasAudio = participant.audioEnabled && Boolean(participant.audioTrack);
  return hasScreenShare || hasCamera || hasAudio;
}

export function getHostParticipant({
  meeting,
}: {
  meeting: RTKClient;
}): RTKParticipant | undefined {
  return meeting.participants.joined
    .toArray()
    .find(
      (participant) =>
        participant.presetName === RTK_HOST_PRESET &&
        !isLivestreamAdminParticipant({ participant }),
    );
}

export function meetingHasHostLivestream({ meeting }: { meeting: RTKClient }): boolean {
  const host = getHostParticipant({ meeting });
  return host ? hasHostLivestream({ participant: host }) : false;
}

export function getStageFeedParticipants({
  meeting,
  includeSelf,
}: {
  meeting: RTKClient;
  includeSelf: boolean;
}): RTKParticipant[] {
  const selfId = meeting.self.id;

  const selfOnStage = meeting.stage.status === "ON_STAGE";

  const onStage = meeting.participants.joined.toArray().filter((participant) => {
    if (isLivestreamAdminParticipant({ participant })) return false;
    if (participant.id === selfId) {
      return includeSelf || participant.stageStatus === "ON_STAGE" || selfOnStage;
    }
    if (participant.stageStatus === "ON_STAGE") return true;
    if (!includeSelf && participant.presetName === RTK_HOST_PRESET) return true;
    return false;
  });

  if (
    (includeSelf || selfOnStage) &&
    !onStage.some((participant) => participant.id === selfId)
  ) {
    onStage.unshift(meeting.self as unknown as RTKParticipant);
  }

  return onStage.sort((a, b) => {
    if (a.id === selfId) return -1;
    if (b.id === selfId) return 1;
    if (a.presetName === RTK_HOST_PRESET && b.presetName !== RTK_HOST_PRESET) return -1;
    if (b.presetName === RTK_HOST_PRESET && a.presetName !== RTK_HOST_PRESET) return 1;
    return a.name.localeCompare(b.name);
  });
}

export function getVideoGridLayout({ count }: { count: number }): {
  cols: number;
  rows: number;
} {
  if (count <= 1) return { cols: 1, rows: 1 };
  return { cols: 1, rows: count };
}
